
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { User, Mail, Phone, MapPin, Building2 } from "lucide-react";
import LoginForm from "@/components/auth/LoginForm";

const validateEmail = (email: string): boolean => {
  console.log("Validating email:", email);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone: string): boolean => {
  console.log("Validating phone:", phone);
  const phoneRegex = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
  return phoneRegex.test(phone);
};

const validateName = (name: string): boolean => {
  const nameRegex = /^[a-zA-ZÀ-ÿ\s'-]{1,50}$/;
  return nameRegex.test(name);
};

const validateAddress = (address: string): boolean => {
  const addressRegex = /^[a-zA-Z0-9À-ÿ\s\-\.,#]{1,100}$/;
  return addressRegex.test(address);
};

const validateCity = (city: string): boolean => {
  const cityRegex = /^[a-zA-ZÀ-ÿ\s\-]{1,50}$/;
  return cityRegex.test(city);
};

const validatePostalCode = (postalCode: string): boolean => {
  console.log("Validating postal code:", postalCode);
  const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
  return postalCodeRegex.test(postalCode);
};

const validateCompany = (company: string): boolean => {
  const companyRegex = /^[a-zA-Z0-9À-ÿ\s\-\.]{0,100}$/;
  return company === "" || companyRegex.test(company);
};

const Account = () => {
  const { user, signIn, signUp, signOut } = useAuth();
  const { toast } = useToast();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // États du formulaire d'inscription
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("Québec");
  const [postalCode, setPostalCode] = useState("");
  const [company, setCompany] = useState("");

  // États des erreurs
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [postalCodeError, setPostalCodeError] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [addressError, setAddressError] = useState("");
  const [cityError, setCityError] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (user) {
      loadProfile();
      setEmail(user.email || '');
    }
  }, [user]);

  const loadProfile = async () => {
    setProfileLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      if (data) {
        setFirstName(data.first_name || '');
        setLastName(data.last_name || '');
        setPhone(data.phone || '');
        setStreetAddress(data.street_address || '');
        setCity(data.city || '');
        setProvince(data.province || 'Québec');
        setPostalCode(data.postal_code || '');
        setCompany(data.company || '');
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger votre profil",
        variant: "destructive"
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const updateProfile = async () => {
    setLoading(true);
    try {
      if (!streetAddress || !city || !province || !postalCode || !phone) {
        throw new Error("Veuillez remplir tous les champs obligatoires");
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
          phone,
          street_address: streetAddress,
          city,
          province,
          postal_code: postalCode,
          company
        })
        .eq('id', user.id);

      if (error) throw error;

      toast({
        title: "Profil mis à jour",
        description: "Vos informations ont été enregistrées"
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    let isValid = true;

    // Reset des erreurs
    setEmailError("");
    setPhoneError("");
    setFirstNameError("");
    setLastNameError("");
    setAddressError("");
    setCityError("");
    setPostalCodeError("");
    setCompanyError("");
    
    if (!validateName(firstName)) {
      setFirstNameError("Prénom invalide (max 50 caractères, lettres et tirets uniquement)");
      isValid = false;
    }
    if (!validateName(lastName)) {
      setLastNameError("Nom invalide (max 50 caractères, lettres et tirets uniquement)");
      isValid = false;
    }
    if (!validateAddress(streetAddress)) {
      setAddressError("Adresse invalide");
      isValid = false;
    }
    if (!validateCity(city)) {
      setCityError("Ville invalide");
      isValid = false;
    }
    if (!validatePostalCode(postalCode)) {
      setPostalCodeError("Code postal invalide (ex: H1X 1X1)");
      isValid = false;
    }
    if (!validatePhone(phone)) {
      setPhoneError("Numéro de téléphone invalide (ex: (514) 555-0123)");
      isValid = false;
    }
    if (company && !validateCompany(company)) {
      setCompanyError("Nom de compagnie invalide");
      isValid = false;
    }
    if (!validateEmail(email)) {
      setEmailError("Adresse email invalide");
      isValid = false;
    }

    if (!isValid) {
      toast({
        title: "Erreur de validation",
        description: "Veuillez corriger les erreurs dans le formulaire",
        variant: "destructive"
      });
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setFormError("");

    try {
      const result = await signUp({
        email,
        password,
        firstName,
        lastName,
        phone,
        streetAddress,
        city,
        province,
        postalCode,
        company
      });
      
      if (!result.success) {
        setFormError(result.message || "Une erreur est survenue");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setFormError(typeof error === 'string' ? error : "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  if (user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold tracking-tight mb-8">
            Mon Compte
          </h1>
          
          <div className="bg-white shadow rounded-lg p-6 space-y-6">
            {profileLoading ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium mb-1">
                      Prénom *
                    </label>
                    <div className="relative">
                      <Input 
                        id="firstName" 
                        type="text" 
                        value={firstName} 
                        onChange={e => setFirstName(e.target.value)} 
                        required 
                        className="pl-10" 
                        placeholder="Jean" 
                        maxLength={50} 
                      />
                      <User className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    </div>
                    {firstNameError && <p className="text-red-500 text-sm mt-1">{firstNameError}</p>}
                  </div>
                  
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium mb-1">
                      Nom *
                    </label>
                    <div className="relative">
                      <Input 
                        id="lastName" 
                        type="text" 
                        value={lastName} 
                        onChange={e => setLastName(e.target.value)} 
                        required 
                        className="pl-10" 
                        placeholder="Dupont" 
                        maxLength={50} 
                      />
                      <User className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    </div>
                    {lastNameError && <p className="text-red-500 text-sm mt-1">{lastNameError}</p>}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1 flex items-center gap-2">
                    Email
                    <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">Non modifiable</span>
                  </label>
                  <div className="relative">
                    <Input 
                      id="email" 
                      type="email" 
                      value={email} 
                      className="pl-10 bg-gray-50 text-gray-500 cursor-not-allowed" 
                      readOnly 
                      placeholder="jean.dupont@example.com" 
                    />
                    <Mail className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium mb-1">
                    Téléphone *
                  </label>
                  <div className="relative">
                    <Input 
                      id="phone" 
                      type="tel" 
                      value={phone} 
                      onChange={e => setPhone(e.target.value)} 
                      required 
                      className="pl-10" 
                      placeholder="(514) 555-0123" 
                      maxLength={15} 
                    />
                    <Phone className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                  {phoneError && <p className="text-red-500 text-sm mt-1">{phoneError}</p>}
                </div>

                <div>
                  <label htmlFor="streetAddress" className="block text-sm font-medium mb-1">
                    Rue *
                  </label>
                  <div className="relative">
                    <Input 
                      id="streetAddress" 
                      type="text" 
                      value={streetAddress} 
                      onChange={e => setStreetAddress(e.target.value)} 
                      required 
                      className="pl-10" 
                      placeholder="123 rue Principale" 
                      maxLength={100} 
                    />
                    <MapPin className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                  {addressError && <p className="text-red-500 text-sm mt-1">{addressError}</p>}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium mb-1">
                      Ville *
                    </label>
                    <div className="relative">
                      <Input 
                        id="city" 
                        type="text" 
                        value={city} 
                        onChange={e => setCity(e.target.value)} 
                        required 
                        className="pl-10" 
                        placeholder="Montréal" 
                        maxLength={50} 
                      />
                      <MapPin className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    </div>
                    {cityError && <p className="text-red-500 text-sm mt-1">{cityError}</p>}
                  </div>

                  <div>
                    <label htmlFor="province" className="block text-sm font-medium mb-1">
                      Province *
                    </label>
                    <div className="relative">
                      <Input 
                        id="province" 
                        type="text" 
                        value={province} 
                        onChange={e => setProvince(e.target.value)} 
                        required 
                        className="pl-10" 
                        placeholder="Québec" 
                        maxLength={50} 
                      />
                      <MapPin className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="postalCode" className="block text-sm font-medium mb-1">
                    Code postal *
                  </label>
                  <div className="relative">
                    <Input 
                      id="postalCode" 
                      type="text" 
                      value={postalCode} 
                      onChange={e => setPostalCode(e.target.value)} 
                      required 
                      className="pl-10" 
                      placeholder="H1X 1X1" 
                      maxLength={7} 
                    />
                    <MapPin className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                  {postalCodeError && <p className="text-red-500 text-sm mt-1">{postalCodeError}</p>}
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium mb-1">
                    Compagnie (optionnel)
                  </label>
                  <div className="relative">
                    <Input 
                      id="company" 
                      type="text" 
                      value={company} 
                      onChange={e => setCompany(e.target.value)} 
                      className="pl-10" 
                      placeholder="Nom de votre entreprise" 
                      maxLength={100} 
                    />
                    <Building2 className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                  {companyError && <p className="text-red-500 text-sm mt-1">{companyError}</p>}
                </div>

                <div className="flex justify-between items-center pt-4 border-t">
                  <Button onClick={() => signOut()} variant="outline">
                    Se déconnecter
                  </Button>

                  <Button 
                    onClick={updateProfile} 
                    disabled={loading} 
                    className="relative"
                  >
                    {loading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-primary rounded">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      </div>
                    )}
                    {loading ? "Enregistrement..." : "Enregistrer les modifications"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-md mx-auto">
        <h1 className="text-3xl font-bold tracking-tight mb-8 text-center">
          {isLogin ? "Connexion" : "Créer un compte"}
        </h1>
        
        {isLogin ? (
          <LoginForm 
            onSubmit={signIn} 
            onToggleForm={() => setIsLogin(false)} 
            loading={loading} 
          />
        ) : (
          <div className="w-full max-w-md mx-auto p-6 space-y-6 bg-card text-card-foreground shadow-sm rounded-lg border">
            <form onSubmit={handleSubmit} className="space-y-4">
              {formError && (
                <div className="p-3 rounded bg-destructive/15 text-destructive text-sm">
                  {formError}
                </div>
              )}
              
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium mb-1">
                    Prénom *
                  </label>
                  <div className="relative">
                    <Input 
                      id="firstName" 
                      type="text" 
                      value={firstName} 
                      onChange={e => setFirstName(e.target.value)} 
                      required 
                      className="pl-10" 
                      placeholder="Jean" 
                      maxLength={50} 
                    />
                    <User className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                  {firstNameError && <p className="text-red-500 text-sm mt-1">{firstNameError}</p>}
                </div>
                
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium mb-1">
                    Nom *
                  </label>
                  <div className="relative">
                    <Input 
                      id="lastName" 
                      type="text" 
                      value={lastName} 
                      onChange={e => setLastName(e.target.value)} 
                      required 
                      className="pl-10" 
                      placeholder="Dupont" 
                      maxLength={50} 
                    />
                    <User className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                  {lastNameError && <p className="text-red-500 text-sm mt-1">{lastNameError}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1">
                  Email *
                </label>
                <div className="relative">
                  <Input 
                    id="email" 
                    type="email" 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    required 
                    className="pl-10" 
                    placeholder="jean.dupont@example.com" 
                    maxLength={100} 
                  />
                  <Mail className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
                {emailError && <p className="text-red-500 text-sm mt-1">{emailError}</p>}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-1">
                  Mot de passe *
                </label>
                <div className="relative">
                  <Input 
                    id="password" 
                    type="password"
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    required 
                    className="pl-10" 
                    placeholder="••••••••" 
                    maxLength={100} 
                  />
                  <Mail className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium mb-1">
                  Téléphone *
                </label>
                <div className="relative">
                  <Input 
                    id="phone" 
                    type="tel" 
                    value={phone} 
                    onChange={e => setPhone(e.target.value)} 
                    required 
                    className="pl-10" 
                    placeholder="(514) 555-0123" 
                    maxLength={15} 
                  />
                  <Phone className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
                {phoneError && <p className="text-red-500 text-sm mt-1">{phoneError}</p>}
              </div>

              <div>
                <label htmlFor="streetAddress" className="block text-sm font-medium mb-1">
                  Rue *
                </label>
                <div className="relative">
                  <Input 
                    id="streetAddress" 
                    type="text" 
                    value={streetAddress} 
                    onChange={e => setStreetAddress(e.target.value)} 
                    required 
                    className="pl-10" 
                    placeholder="123 rue Principale" 
                    maxLength={100} 
                  />
                  <MapPin className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
                {addressError && <p className="text-red-500 text-sm mt-1">{addressError}</p>}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium mb-1">
                    Ville *
                  </label>
                  <div className="relative">
                    <Input 
                      id="city" 
                      type="text" 
                      value={city} 
                      onChange={e => setCity(e.target.value)} 
                      required 
                      className="pl-10" 
                      placeholder="Montréal" 
                      maxLength={50} 
                    />
                    <MapPin className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                  {cityError && <p className="text-red-500 text-sm mt-1">{cityError}</p>}
                </div>

                <div>
                  <label htmlFor="province" className="block text-sm font-medium mb-1">
                    Province *
                  </label>
                  <div className="relative">
                    <Input 
                      id="province" 
                      type="text" 
                      value={province} 
                      onChange={e => setProvince(e.target.value)} 
                      required 
                      className="pl-10" 
                      placeholder="Québec" 
                      maxLength={50} 
                    />
                    <MapPin className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="postalCode" className="block text-sm font-medium mb-1">
                  Code postal *
                </label>
                <div className="relative">
                  <Input 
                    id="postalCode" 
                    type="text" 
                    value={postalCode} 
                    onChange={e => setPostalCode(e.target.value)} 
                    required 
                    className="pl-10" 
                    placeholder="H1X 1X1" 
                    maxLength={7} 
                  />
                  <MapPin className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
                {postalCodeError && <p className="text-red-500 text-sm mt-1">{postalCodeError}</p>}
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium mb-1">
                  Compagnie (optionnel)
                </label>
                <div className="relative">
                  <Input 
                    id="company" 
                    type="text" 
                    value={company} 
                    onChange={e => setCompany(e.target.value)} 
                    className="pl-10" 
                    placeholder="Nom de votre entreprise" 
                    maxLength={100} 
                  />
                  <Building2 className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
                {companyError && <p className="text-red-500 text-sm mt-1">{companyError}</p>}
              </div>

              <div className="flex justify-between items-center pt-4">
                <Button 
                  type="submit" 
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Chargement...
                    </span>
                  ) : "Créer un compte"}
                </Button>

                <button
                  type="button"
                  onClick={() => setIsLogin(true)}
                  className="text-sm text-primary hover:underline"
                >
                  Déjà un compte ?
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Account;
